import type { AnalyticsEventRecord, AnalyticsProperty } from './types';

export function findProperty(event: AnalyticsEventRecord | undefined, key: string): AnalyticsProperty | undefined {
  return event?.properties.find((property) => property.key === key);
}

export function readNumber(event: AnalyticsEventRecord | undefined, key: string): number {
  const value = findProperty(event, key)?.value;
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

export function readString(event: AnalyticsEventRecord | undefined, key: string): string {
  const value = findProperty(event, key)?.value;
  return typeof value === 'string' ? value : '';
}

export function readObject(event: AnalyticsEventRecord | undefined, key: string): Record<string, unknown> {
  const value = findProperty(event, key)?.value;
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return {};
  }
  return value as Record<string, unknown>;
}

export function formatPropertyValue(property: AnalyticsProperty): string {
  if (property.value === null || property.value === undefined) {
    return '—';
  }
  if (property.enumDisplayName !== null) {
    return property.enumDisplayName;
  }
  if (property.type === 'bool') {
    return property.value === true ? 'Yes' : 'No';
  }
  if (property.type === 'json') {
    return JSON.stringify(property.value);
  }
  const suffix = property.unit === null ? '' : ` ${property.unit}`;
  return `${String(property.value)}${suffix}`;
}

export function formatEventOccurrenceName(event: AnalyticsEventRecord): string {
  for (const property of event.properties) {
    if (property.enumDisplayName !== null) {
      return `${event.eventName} — ${property.enumDisplayName}`;
    }
  }
  return event.eventName;
}

export function formatDynamicValue(value: unknown): string {
  if (typeof value === 'number') {
    return new Intl.NumberFormat().format(value);
  }
  if (typeof value === 'string' || typeof value === 'boolean') {
    return String(value);
  }
  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
    return Object.entries(value)
      .map(([key, nested]) => `${humanize(key)} ${String(nested)}`)
      .join(' · ');
  }
  return JSON.stringify(value);
}

export function humanize(value: string): string {
  return value.replaceAll('_', ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}
